import { BillingAPI } from './API'
import Coupon from './models/Coupon'
import { collection } from '../infra/collection'
import {
  CouponDTO,
  FindCouponQuery,
  CreateCouponPayload,
  UpdateCouponPayload,
  UserCouponDTO,
  AddUserCouponPayload,
} from './dtos/coupon'
import UserCoupon from './models/UserCoupon'
import { ignore404, rethrowRequestError } from 'utils/error-util'
import { Id } from 'types/definitions'
import Member from '../iam/models/Member'

/**
 * Coupon store.
 */
export default class CouponStore {
  /**
   * The coupons themselves.
   */
  coupons = collection<CouponDTO, Coupon>({
    create: () => new Coupon(),
  })

  /** s
   * The user<->coupon relations.
   */
  userCoupons = collection<UserCouponDTO, UserCoupon>({
    create: () => new UserCoupon(this.coupons.set),
  })

  constructor(private api: BillingAPI) {}

  forUser(userId: Id) {
    return this.userCoupons.filter((x) => x.userId === userId)
  }

  async fetchUserCoupons(member: Member) {
    const data = await this.api.userCoupon.getCouponsForUser(
      Id.assert(member.workspace.id),
      Id.assert(member.user.publicId),
    )
    return this.userCoupons.set(data)
  }

  async addCouponToUser(
    member: Member,
    code: string,
  ): Promise<UserCoupon | null> {
    const payload: AddUserCouponPayload = {
      user_public_id: Id.assert(member.user.publicId),
      workspace_id: Id.assert(member.workspace.id),
    }

    return this.api.userCoupon
      .addCouponToUser(code, payload)
      .catch(ignore404)
      .catch(rethrowRequestError)
      .then(this.userCoupons.set)
  }

  forWorkspace(workspaceId: string) {
    return this.coupons.filter((x) => x.workspaceId === workspaceId)
  }

  byCode(code: string) {
    return this.coupons.find((x) => x.code === code)
  }

  async fetchCoupon(id: string) {
    return this.api.coupon.get(id).then((data) => this.coupons.set(data))
  }

  async fetchCouponByCode(code: string, workspaceId: Id) {
    return this.api.coupon
      .getCouponByCode(code, Id.assert(workspaceId))
      .then((data) => this.coupons.set(data))
  }

  async fetchCoupons(query: FindCouponQuery) {
    const cleanQuery: FindCouponQuery = {
      ...query,
    }
    const result = await this.api.coupon.find(cleanQuery)
    return {
      ...result,
      data: this.coupons.set(result.data),
    }
  }

  async createCoupon(data: CreateCouponPayload) {
    const result = await this.api.coupon.create({
      ...data,
    })
    return this.coupons.set(result)
  }

  async updateCoupon(id: string, data: UpdateCouponPayload) {
    const result = await this.api.coupon.update(id, data)
    return this.coupons.set(result)
  }

  async deleteCoupon(id: string) {
    await this.api.coupon.remove(id)
    this.coupons.remove(id)
  }

  couponsForMember(member: Member) {
    return this.userCoupons.filter((uc) => member.is(uc.workspaceId, uc.userId))
  }
}
